import axios from 'axios';
import getApiClient from 'shared/data/helpers/ApiClient';

const apiClient = getApiClient();

export default {
  getColumnTitles(teamId, eventId, showId, itemSetListId) {
    return axios
      .post('/api/itemSetList.getColumnTitles', {
        params: { teamId, eventId, showId, itemSetListId },
      })
      .then((res) => res.data);
  },

  getColumns(teamId, eventId, showId, itemSetListId) {
    return axios
      .post('/api/itemSetList.getColumns', {
        params: { teamId, eventId, showId, itemSetListId },
      })
      .then((res) => res.data);
  },

  getRowOrder(teamId, eventId, showId, itemSetListId) {
    return apiClient.itemSetList.getRowOrder(
      teamId,
      eventId,
      showId,
      itemSetListId,
    );
  },

  get(teamId, eventId, showId) {
    return axios
      .post('/api/show.getItemSetList', {
        params: { teamId, eventId, showId },
      })
      .then((res) => res.data);
  },

  /*
   * If an empty array or no argument is passed in for rows, all rows will be returned
   * If null is passed for rows, no row data will be returned
   * Else any rowIds passed will be returned
   */
  getSubset(teamId, eventId, showId, columns, rows) {
    const query = { rows };
    //Only send `columns` when set. A null/undefined columns must be OMITTED, not
    //sent as null: the proxy serializes it to `columns=`, which the api coerces
    //(Joi .single()) to [''] and the subset handler reads as "return no cells" —
    //clearing every cell on the row. Omitting it makes the handler skip the
    //column filter and return all cells (see Show.getItemSetListSubset).
    if (columns) query.columns = columns;
    return apiClient.show.getItemSetListSubset(teamId, eventId, showId, query);
  },

  getSubsetForGuest(teamId, eventId, showId, columns, rows) {
    return axios
      .post('/api/show.getItemSetListSubsetForGuest', {
        params: { teamId, eventId, showId },
        query: { columns, rows },
      })
      .then((res) => res.data);
  },

  /**
   * Get For Guest Pass
   */
  getForGuest(teamId, eventId, showId, query) {
    return axios
      .post('/api/show.getItemSetListForGuest', {
        params: { teamId, eventId, showId },
        query,
      })
      .then((res) => res.data);
  },

  updateColumnWidth(teamId, eventId, showId, itemSetListId, columnId, width) {
    return _updateColumn(teamId, eventId, showId, itemSetListId, columnId, {
      width,
    });
  },

  updateColumn: _updateColumn,

  hideUserColumn(teamId, eventId, showId, itemSetListId, userId, columnId) {
    return _setUserColumnVisibility(
      teamId,
      eventId,
      showId,
      itemSetListId,
      userId,
      columnId,
      true,
    );
  },

  showUserColumn(teamId, eventId, showId, itemSetListId, userId, columnId) {
    return _setUserColumnVisibility(
      teamId,
      eventId,
      showId,
      itemSetListId,
      userId,
      columnId,
      false,
    );
  },

  showUserColumns(teamId, eventId, showId, itemSetListId, userId) {
    return axios
      .post('/api/itemSetList.showHiddenUserColumns', {
        params: { teamId, eventId, showId, itemSetListId },
        payload: { userId },
      })
      .then((res) => res.data);
  },

  createColumn(teamId, eventId, showId, itemSetListId, column) {
    return axios
      .post('/api/itemSetList.createColumn', {
        params: { teamId, eventId, showId, itemSetListId },
        payload: { column },
      })
      .then((res) => res.data);
  },

  restoreColumn(teamId, eventId, showId, itemSetListId, columnId) {
    return axios
      .post('/api/itemSetList.restoreColumn', {
        params: { teamId, eventId, showId, itemSetListId },
        payload: { columnId },
      })
      .then((res) => res.data);
  },

  softDeleteColumn(teamId, eventId, showId, itemSetListId, columnId) {
    return axios
      .post('/api/itemSetList.softDeleteColumn', {
        params: { teamId, eventId, showId, itemSetListId },
        payload: { columnId },
      })
      .then((res) => res.data);
  },

  softDeleteColumnGuestPass(teamId, eventId, showId, itemSetListId, columnId) {
    return axios
      .post('/api/itemSetList.softDeleteColumnGuestPass', {
        params: { teamId, eventId, showId, itemSetListId },
        payload: { columnId },
      })
      .then((res) => res.data);
  },

  addColumnGuestPass(teamId, eventId, showId, itemSetListId, columnId) {
    return axios
      .post('/api/itemSetList.addColumnGuestPass', {
        params: { teamId, eventId, showId, itemSetListId },
        payload: { columnId },
      })
      .then((res) => res.data);
  },

  moveUserColumn(
    teamId,
    eventId,
    showId,
    itemSetListId,
    userId,
    columnId,
    afterColumnId,
  ) {
    return axios
      .post('/api/itemSetList.moveUserColumn', {
        params: { teamId, eventId, showId, itemSetListId },
        payload: { userId, columnId, afterColumnId },
      })
      .then((res) => res.data);
  },

  moveGuestColumn(
    teamId,
    eventId,
    showId,
    itemSetListId,
    userId,
    columnId,
    afterColumnId,
  ) {
    return axios
      .post('/api/itemSetList.moveGuestColumn', {
        params: { teamId, eventId, showId, itemSetListId },
        payload: { userId, columnId, afterColumnId },
      })
      .then((res) => res.data);
  },

  moveColumn(teamId, eventId, showId, itemSetListId, columnId, afterColumnId) {
    return axios
      .post('/api/itemSetList.moveColumn', {
        params: { teamId, eventId, showId, itemSetListId },
        payload: { columnId, afterColumnId },
      })
      .then((res) => res.data);
  },

  setColumnOrderFromUser(teamId, eventId, showId, itemSetListId, userId) {
    return axios
      .post('/api/itemSetList.setColumnOrderFromUser', {
        params: { teamId, eventId, showId, itemSetListId },
        payload: { userId },
      })
      .then((res) => res.data);
  },

  updateUserSettings(teamId, eventId, showId, itemSetListId, userId, changes) {
    return axios
      .post('/api/itemSetList.updateUserSettings', {
        params: { teamId, eventId, showId, itemSetListId },
        payload: { userId, changes },
      })
      .then((res) => res.data);
  },

  updateUserNoteColumnWidth(
    teamId,
    eventId,
    showId,
    itemSetListId,
    userId,
    width,
  ) {
    return _updateUserNoteColumnSettings(
      teamId,
      eventId,
      showId,
      itemSetListId,
      userId,
      { width },
    );
  },

  hideUserNoteColumn(teamId, eventId, showId, itemSetListId, userId) {
    return _updateUserNoteColumnSettings(
      teamId,
      eventId,
      showId,
      itemSetListId,
      userId,
      { hidden: true },
    );
  },

  showUserNoteColumn(teamId, eventId, showId, itemSetListId, userId) {
    return _updateUserNoteColumnSettings(
      teamId,
      eventId,
      showId,
      itemSetListId,
      userId,
      { hidden: false },
    );
  },

  hideItemColumnDescriptions(teamId, eventId, showId, itemSetListId) {
    return _updateItemColumnSettings(teamId, eventId, showId, itemSetListId, {
      displayDescriptions: false,
    });
  },

  showItemColumnDescriptions(teamId, eventId, showId, itemSetListId) {
    return _updateItemColumnSettings(teamId, eventId, showId, itemSetListId, {
      displayDescriptions: true,
    });
  },

  updateItemColumnWidth(teamId, eventId, showId, itemSetListId, width) {
    return _updateItemColumnSettings(teamId, eventId, showId, itemSetListId, {
      width,
    });
  },

  updateItemTitleColumnWidth(teamId, eventId, showId, itemSetListId, width) {
    return _updateItemColumnSettings(
      teamId,
      eventId,
      showId,
      itemSetListId,
      { width },
      'itemTitleColumn',
    );
  },

  updateItemDescriptionColumnWidth(
    teamId,
    eventId,
    showId,
    itemSetListId,
    width,
  ) {
    return _updateItemColumnSettings(
      teamId,
      eventId,
      showId,
      itemSetListId,
      { width },
      'itemDescriptionColumn',
    );
  },
};

function _setUserColumnVisibility(
  teamId,
  eventId,
  showId,
  itemSetListId,
  userId,
  columnId,
  hidden,
) {
  return axios
    .post('/api/itemSetList.updateUserColumnVisibility', {
      params: { teamId, eventId, showId, itemSetListId },
      payload: { userId, columnId, hidden },
    })
    .then((res) => res.data);
}

function _updateItemColumnSettings(
  teamId,
  eventId,
  showId,
  itemSetListId,
  changes,
  fieldName = 'itemColumn',
) {
  return axios
    .post('/api/itemSetList.updateItemSettings', {
      params: { teamId, eventId, showId, itemSetListId },
      payload: { changes, fieldName },
    })
    .then((res) => res.data);
}

function _updateUserNoteColumnSettings(
  teamId,
  eventId,
  showId,
  itemSetListId,
  userId,
  changes,
) {
  return axios
    .post('/api/itemSetList.updateUserNoteSettings', {
      params: { teamId, eventId, showId, itemSetListId },
      payload: { userId, changes },
    })
    .then((res) => res.data);
}

function _updateColumn(
  teamId,
  eventId,
  showId,
  itemSetListId,
  columnId,
  changes,
) {
  return axios
    .post('/api/itemSetList.updateColumn', {
      params: { teamId, eventId, showId, itemSetListId },
      payload: { columnId, changes },
    })
    .then((res) => res.data);
}
